import React, { useState, useEffect } from 'react';
import { useConsent } from '../context/ConsentContext';
import { ShieldCheck, Cookie, Settings2, Check, Lock } from 'lucide-react';
import { DOMAIN_CONFIG } from '../config';

export const CookieConsentBanner: React.FC = () => {
  const {
    state,
    isBannerVisible,
    isSettingsOpen,
    openSettings,
    closeSettings,
    acceptAll,
    rejectAll,
    saveCustomPreferences,
  } = useConsent();

  // Local state for granular customization
  const [analyticsEnabled, setAnalyticsEnabled] = useState(state.preferences.analytics);
  const [preferencesEnabled, setPreferencesEnabled] = useState(state.preferences.preferences);

  // Sync local toggles whenever state preferences change or settings open
  useEffect(() => {
    setAnalyticsEnabled(state.preferences.analytics);
    setPreferencesEnabled(state.preferences.preferences);
  }, [state.preferences, isSettingsOpen]);

  // The modal/backdrop is shown if user hasn't interacted yet OR if settings are explicitly opened
  const isOpen = isBannerVisible || isSettingsOpen;

  if (!isOpen) {
    return null;
  }

  const handleSaveCustom = () => {
    saveCustomPreferences({
      necessary: true,
      analytics: analyticsEnabled,
      preferences: preferencesEnabled,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-slate-950/85 backdrop-blur-md animate-fadeIn"
    >
      <div className="relative w-full max-w-2xl bg-[#090d1a] border border-slate-800/90 rounded-2xl shadow-2xl shadow-black/80 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header decoration bar */}
        <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500" />

        {/* Modal Content */}
        <div className="p-6 sm:p-8 overflow-y-auto">
          {/* Header Title */}
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 shrink-0">
              <Cookie className="w-6 h-6" />
            </div>
            <div>
              <h2
                id="cookie-consent-title"
                className="text-lg sm:text-xl font-bold text-white tracking-tight"
              >
                Privacy & Cookie Preferences
              </h2>
              <p className="text-xs font-mono text-emerald-400/90 mt-0.5">
                {DOMAIN_CONFIG.domainName}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="text-xs sm:text-sm text-slate-300 leading-relaxed space-y-2 mb-6">
            <p>
              We use cookies and similar technologies to ensure site security, analyze traffic and engagement, and provide a reliable browsing experience.
            </p>
            <p className="text-slate-400 text-xs">
              You can choose to <strong className="text-slate-200">Accept all</strong> to enable Google Analytics tracking, <strong className="text-slate-200">Reject all</strong> optional cookies, or configure granular preferences. You can update or disable Google Analytics at any time using the Cookie Settings link in the footer.
            </p>
          </div>

          {/* Granular Preferences View (Shown if customizing or settings opened) */}
          {isSettingsOpen ? (
            <div className="space-y-4 mb-6 pt-2 border-t border-slate-800/80">
              <div className="flex items-center justify-between pb-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                  Custom Cookie Controls
                </span>
                <span className="text-[11px] text-slate-500">
                  Select optional categories
                </span>
              </div>

              {/* 1. Necessary (Always Active) */}
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">Strictly Necessary</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono font-medium px-2 py-0.5 rounded-md bg-emerald-950/80 border border-emerald-500/30 text-emerald-400">
                      <Lock className="w-3 h-3" /> Always Active
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Essential for website security, bot mitigation (Cloudflare Turnstile), and saving your privacy preferences. These cannot be disabled.
                  </p>
                </div>
                <div className="pt-1">
                  <div className="w-10 h-6 bg-emerald-600/50 rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-80">
                    <div className="w-4 h-4 bg-emerald-400 rounded-full flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-slate-950 stroke-[3]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Analytics (Google Analytics) */}
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 hover:border-slate-700/80 transition-colors flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">Google Analytics (GA4)</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-800 text-slate-300">
                      Optional
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Loads Google Analytics script (<code className="text-slate-300">gtag.js</code>) to measure page visits, interaction with domain acquisition inquiries, and referrer sources. IP addresses are anonymized.
                  </p>
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={analyticsEnabled}
                    onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                    className={`w-11 h-6 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-emerald-400/50 ${
                      analyticsEnabled ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out absolute top-1 ${
                        analyticsEnabled ? 'left-6' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* 3. Site Preferences & Functionality */}
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 hover:border-slate-700/80 transition-colors flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">Functionality & Preferences</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-800 text-slate-300">
                      Optional
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Remembers your UI settings, interactive form drafts, and display options across visits.
                  </p>
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={preferencesEnabled}
                    onClick={() => setPreferencesEnabled(!preferencesEnabled)}
                    className={`w-11 h-6 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-emerald-400/50 ${
                      preferencesEnabled ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out absolute top-1 ${
                        preferencesEnabled ? 'left-6' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-3 border-t border-slate-800/80">
            {isSettingsOpen ? (
              <>
                {state.hasInteracted && (
                  <button
                    type="button"
                    onClick={closeSettings}
                    className="order-3 sm:order-1 px-4 py-2.5 rounded-xl border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={rejectAll}
                  className="order-2 px-4 py-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-semibold transition-colors"
                >
                  Reject all
                </button>
                <button
                  type="button"
                  onClick={handleSaveCustom}
                  className="order-1 sm:order-3 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold transition-all shadow-lg shadow-emerald-500/20"
                >
                  Save preferences
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={openSettings}
                  className="order-3 sm:order-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-900/60 text-slate-300 hover:text-white text-xs font-medium transition-colors"
                >
                  <Settings2 className="w-3.5 h-3.5 text-slate-400" />
                  <span>Customize</span>
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="order-2 px-5 py-2.5 rounded-xl bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-slate-200 hover:text-white text-xs font-semibold transition-colors"
                >
                  Reject all
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="order-1 sm:order-3 inline-flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
                >
                  <ShieldCheck className="w-4 h-4 text-slate-950" />
                  <span>Accept all</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
